'use client'
import { useState } from 'react'
import { signIn } from 'next-auth/react'
import { User, Eye } from 'lucide-react'
import { useTranslations } from '@/lib/i18n/I18nProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export function LoginForm({ redirectTo = '/projects' }: { redirectTo?: string }) {
  const { t } = useTranslations()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setPending(true)

    const form = new FormData(event.currentTarget)
    const result = await signIn('credentials', {
      email: String(form.get('email') ?? ''),
      password: String(form.get('password') ?? ''),
      redirect: false,
    })

    if (!result || result.error) {
      setError(t('auth.errors.invalidCredentials'))
      setPending(false)
      return
    }

    // Full navigation so SessionProvider re-initializes with the authenticated
    // session. An in-app (RSC) transition keeps the stale client session, which
    // leaves permission-gated UI (e.g. the sidebar) hidden until a manual refresh.
    window.location.href = redirectTo
  }

  return (
    <form onSubmit={onSubmit} className="w-full max-w-[360px]">
      <h1 className="text-2xl font-semibold text-foreground">{t('auth.login.title')}</h1>
      <p className="mt-1 mb-7 text-sm text-muted-foreground">{t('auth.login.subtitle')}</p>

      <label htmlFor="login-email" className="mb-1.5 block text-sm text-foreground">
        {t('auth.login.email')}
      </label>
      <div className="relative">
        <User
          size={15}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
        />
        <Input id="login-email" name="email" type="email" required autoComplete="email" className="h-11 pl-9" />
      </div>

      <div className="h-4" />

      <label htmlFor="login-password" className="mb-1.5 block text-sm text-foreground">
        {t('auth.login.password')}
      </label>
      <div className="relative">
        <Eye
          size={15}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id="login-password"
          name="password"
          type="password"
          required
          autoComplete="current-password"
          placeholder="••••••••"
          className="h-11 pl-9"
        />
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={pending}
        className="mt-6 h-11 w-full"
      >
        {pending ? t('auth.login.submitting') : t('auth.login.submit')}
      </Button>
    </form>
  )
}
